import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I Base64 encode XML?', a: 'Paste your XML above and switch to Encode. The tool treats it as UTF-8 text and returns a Base64 string safe to embed in JSON, headers or config files.' },
  { q: 'Why Base64 encode XML at all?', a: 'To move XML safely through channels that mishandle angle brackets, quotes or namespaces — for example embedding an XML payload inside a JSON field, a SOAP attachment, or a URL parameter.' },
  { q: 'Does encoding XML to Base64 keep it valid?', a: 'Decoding returns the exact original XML text, character for character, so it stays valid as long as your input was valid XML.' },
  { q: 'Is this the same as XML canonicalization (C14N)?', a: 'No. Base64 just makes XML text-safe for transport; it does not normalize whitespace, attribute order or namespaces the way C14N does for XML digital signatures.' },
];

export default function XmlToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="XML to Base64 — Encode & Decode XML Online | EasyBase64"
        description="Encode XML to Base64 and decode it back online for free. UTF-8 safe, instant, and fully private. Useful for SOAP attachments, config files and APIs."
        path="/xml-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'XML to Base64 Converter', description: 'Encode and decode XML as Base64 in the browser.', path: '/xml-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'XML to Base64', path: '/xml-to-base64' }]),
        ]}
      />
      <PageHero
        title="XML to Base64"
        subtitle={<>Encode XML to Base64 and decode it back. Need JSON instead? Try <Link to="/json-to-base64" className="underline">JSON to Base64</Link>.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Example</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed mb-4`}>
          The XML <code>{'<user id="7"/>'}</code> encodes to <code>PHVzZXIgaWQ9IjciLz4=</code>. In code you would do:
        </p>
        <CodeBlock
          language="JavaScript"
          code={`const xml = '<user id="7"/>';
const b64 = btoa(unescape(encodeURIComponent(xml))); // "PHVzZXIgaWQ9IjciLz4="
const back = decodeURIComponent(escape(atob(b64)));`}
        />
      </section>
      <FaqSection title="XML to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
